import { Building2, Check, ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  organizationLabel,
  useOrganization,
} from "@/context/OrganizationContext";
import {
  SensitiveIdentity,
  usePrivacyMasking,
} from "@/components/common/Sensitive";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface OrganizationPillProps {
  className?: string;
}

function OrgName({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  return (
    <span className={cn("flex items-center gap-2", className)}>
      <Building2 className="size-4 shrink-0 text-primary" />
      <SensitiveIdentity value={name} className="max-w-[10rem] truncate" />
    </span>
  );
}

/** Global org switcher in the top bar. */
export function OrganizationPill({ className }: OrganizationPillProps) {
  const {
    organization,
    organizations,
    currentClientKey,
    canSwitch,
    switchOrganization,
  } = useOrganization();
  const hide = usePrivacyMasking();

  return (
    <div
      className={cn(
        "shell-chrome glass-pill clay glass-no-hover flex w-fit items-center gap-2 p-1.5",
        className,
      )}
    >
      {canSwitch ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className="h-9 gap-2 rounded-full px-2 text-sm hover:bg-accent"
              aria-label="Switch organization"
              aria-haspopup="listbox"
            >
              <OrgName name={organization} />
              <ChevronDown className="size-4 shrink-0 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            side="bottom"
            align="center"
            sideOffset={14}
            className="w-56 p-1"
          >
            {organizations.map((org) => {
              const label = organizationLabel(org);
              const isCurrent = org.client_key === currentClientKey;
              return (
                <button
                  key={org.auth0_org_id || org.client_key}
                  type="button"
                  onClick={() => {
                    if (isCurrent || !org.auth0_org_id) return;
                    switchOrganization(org.auth0_org_id);
                  }}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors",
                    isCurrent
                      ? "cursor-default text-foreground"
                      : "hover:bg-accent",
                  )}
                >
                  <SensitiveIdentity value={label} className="truncate" />
                  {isCurrent && (
                    <Check className="size-4 shrink-0 text-primary" />
                  )}
                </button>
              );
            })}
          </PopoverContent>
        </Popover>
      ) : (
        <div
          className="flex h-9 items-center rounded-full px-2 text-sm"
          role="status"
          aria-label={hide ? "Organization" : `Organization: ${organization}`}
        >
          <OrgName name={organization} />
        </div>
      )}
    </div>
  );
}
